/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, RotateCcw } from 'lucide-react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS_OF_WEEK = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Formats a Date object to YYYY-MM-DD string using local calendar parts.
 */
export const formatDateToYMD = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Parses a YYYY-MM-DD string into a local Date object.
 */
export const parseYMDToLocalDate = (ymd) => {
  if (!ymd || typeof ymd !== 'string') return null;
  const parts = ymd.split('-');
  if (parts.length !== 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
  return new Date(y, m, d, 0, 0, 0, 0);
};

/**
 * Formats a date range into a readable user-friendly string.
 */
const formatDisplayRange = (startStr, endStr) => {
  if (!startStr && !endStr) return '';
  const startDate = parseYMDToLocalDate(startStr);
  const endDate = parseYMDToLocalDate(endStr);

  const formatSingle = (date, includeYear = true) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: includeYear ? 'numeric' : undefined,
    });
  };

  if (startDate && endDate) {
    if (startStr === endStr) {
      return formatSingle(startDate, true);
    }
    const sameYear = startDate.getFullYear() === endDate.getFullYear();
    const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

    if (sameMonth) {
      return `${startDate.toLocaleDateString('en-US', { month: 'short' })} ${startDate.getDate()} - ${endDate.getDate()}, ${startDate.getFullYear()}`;
    }
    if (sameYear) {
      return `${formatSingle(startDate, false)} - ${formatSingle(endDate, true)}`;
    }
    return `${formatSingle(startDate, true)} - ${formatSingle(endDate, true)}`;
  }

  if (startDate) return `From ${formatSingle(startDate, true)}`;
  if (endDate) return `Until ${formatSingle(endDate, true)}`;
  return '';
};

const DateRangePicker = ({
  startDate = '',
  endDate = '',
  onChange,
  placeholder = 'Filter by date range',
  className = '',
  disabled = false,
  align = 'auto',
  maxDate = formatDateToYMD(new Date()),
  minDate = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const [effectiveAlign, setEffectiveAlign] = useState(align === 'right' ? 'right' : 'left');
  const [horizontalShift, setHorizontalShift] = useState(0);

  // Local selection state inside the picker before applying/completing
  const [pendingStart, setPendingStart] = useState(startDate);
  const [pendingEnd, setPendingEnd] = useState(endDate);
  const [hoveredDate, setHoveredDate] = useState(null);

  // Helper to determine initial left month: default to previous month so current month is on the right
  const getDefaultViewDate = (start) => {
    const today = new Date();
    if (start) {
      const d = parseYMDToLocalDate(start);
      if (d) {
        if (d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth()) {
          return new Date(today.getFullYear(), today.getMonth() - 1, 1);
        }
        return new Date(d.getFullYear(), d.getMonth(), 1);
      }
    }
    return new Date(today.getFullYear(), today.getMonth() - 1, 1);
  };

  // Calendar view month: stores Date corresponding to the 1st of the left/primary month (previous month by default)
  const [viewDate, setViewDate] = useState(() => getDefaultViewDate(startDate));

  // Automatically determine popover alignment and adjust to stay within viewport bounds
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const popoverEl = popoverRef.current;
      const popoverWidth = popoverEl ? popoverEl.offsetWidth : 660;
      const viewportWidth = window.innerWidth;
      const margin = 16; // minimum spacing from viewport edge

      const spaceRight = viewportWidth - rect.left - margin;
      const spaceLeft = rect.right - margin;

      let chosenAlign = 'left';

      if (align === 'left') {
        chosenAlign = 'left';
      } else if (align === 'right') {
        // If explicitly set to right, but left space is insufficient and right has enough space, flip to left
        if (spaceLeft < popoverWidth && spaceRight >= popoverWidth) {
          chosenAlign = 'left';
        } else {
          chosenAlign = 'right';
        }
      } else {
        // 'auto' mode:
        // If placing on left (extending right) fits within viewport, prefer 'left' (avoids overlapping sidebar on the left)
        if (spaceRight >= popoverWidth) {
          chosenAlign = 'left';
        } else if (spaceLeft >= popoverWidth) {
          chosenAlign = 'right';
        } else {
          // If neither side fits fully without overflow, choose side with more available space
          chosenAlign = spaceRight >= spaceLeft ? 'left' : 'right';
        }
      }

      // Calculate any subtle shift required to keep within viewport bounds
      let shift = 0;
      if (chosenAlign === 'left') {
        const popoverRight = rect.left + popoverWidth;
        if (popoverRight > viewportWidth - margin) {
          shift = (viewportWidth - margin) - popoverRight;
          if (rect.left + shift < margin) {
            shift = margin - rect.left;
          }
        }
      } else {
        // chosenAlign === 'right'
        const popoverLeft = rect.right - popoverWidth;
        if (popoverLeft < margin) {
          shift = margin - popoverLeft;
          if (rect.right + shift > viewportWidth - margin) {
            shift = (viewportWidth - margin) - rect.right;
          }
        }
      }

      setEffectiveAlign(chosenAlign);
      setHorizontalShift(shift);
    };

    updatePosition();
    const rafId = requestAnimationFrame(updatePosition);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, align]);

  // Synchronize internal state when props change
  useEffect(() => {
    setPendingStart(startDate);
    setPendingEnd(endDate);
    if (startDate) {
      setViewDate(getDefaultViewDate(startDate));
    }
  }, [startDate, endDate]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        // Reset pending selection to committed values
        setPendingStart(startDate);
        setPendingEnd(endDate);
        setHoveredDate(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setPendingStart(startDate);
        setPendingEnd(endDate);
        setHoveredDate(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, startDate, endDate]);

  const handlePrevMonth = () => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const isNextMonthDisabled = useMemo(() => {
    if (!maxDate) return false;
    const maxDateObj = parseYMDToLocalDate(maxDate);
    if (!maxDateObj) return false;
    const maxMonthStart = new Date(maxDateObj.getFullYear(), maxDateObj.getMonth(), 1);
    const currentNextMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
    return currentNextMonth >= maxMonthStart;
  }, [viewDate, maxDate]);

  const handleNextMonth = () => {
    if (isNextMonthDisabled) return;
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleDateClick = (ymd) => {
    if (maxDate && ymd > maxDate) return;
    if (minDate && ymd < minDate) return;
    if (!pendingStart || (pendingStart && pendingEnd)) {
      // Start a new range selection
      setPendingStart(ymd);
      setPendingEnd('');
      setHoveredDate(null);
    } else {
      // We have pendingStart and are setting pendingEnd
      if (ymd < pendingStart) {
        // Clicked date is earlier than start: restart range with new date
        setPendingStart(ymd);
        setPendingEnd('');
      } else {
        // Complete the range
        setPendingEnd(ymd);
        setHoveredDate(null);
        if (onChange) {
          onChange({ startDate: pendingStart, endDate: ymd });
        }
        setIsOpen(false);
      }
    }
  };

  const handleApplyPreset = (presetStart, presetEnd) => {
    setPendingStart(presetStart);
    setPendingEnd(presetEnd);
    setHoveredDate(null);
    if (presetStart) {
      setViewDate(getDefaultViewDate(presetStart));
    }
    if (onChange) {
      onChange({ startDate: presetStart, endDate: presetEnd });
    }
    setIsOpen(false);
  };

  const handleClear = (e) => {
    if (e) e.stopPropagation();
    setPendingStart('');
    setPendingEnd('');
    setHoveredDate(null);
    setViewDate(getDefaultViewDate(''));
    if (onChange) {
      onChange({ startDate: '', endDate: '' });
    }
  };

  const handleReset = (e) => {
    if (e) e.stopPropagation();
    setPendingStart('');
    setPendingEnd('');
    setHoveredDate(null);
    setViewDate(getDefaultViewDate(''));
    if (onChange) {
      onChange({ startDate: '', endDate: '' });
    }
    setIsOpen(false);
  };

  const presets = useMemo(() => {
    const today = new Date();
    const todayYMD = formatDateToYMD(today);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const yesterdayYMD = formatDateToYMD(yesterday);

    const last7 = new Date(today);
    last7.setDate(today.getDate() - 6);
    const last7YMD = formatDateToYMD(last7);

    const last30 = new Date(today);
    last30.setDate(today.getDate() - 29);
    const last30YMD = formatDateToYMD(last30);

    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const thisMonthEndYMD = todayYMD;

    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

    return [
      { label: 'Today', start: todayYMD, end: todayYMD },
      { label: 'Yesterday', start: yesterdayYMD, end: yesterdayYMD },
      { label: 'Last 7 Days', start: last7YMD, end: todayYMD },
      { label: 'Last 30 Days', start: last30YMD, end: todayYMD },
      { label: 'This Month', start: formatDateToYMD(thisMonthStart), end: thisMonthEndYMD },
      { label: 'Last Month', start: formatDateToYMD(lastMonthStart), end: formatDateToYMD(lastMonthEnd) },
    ];
  }, []);

  // Compute calendar days for a specific month
  const getMonthData = (year, month) => {
    const firstDayIndex = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const prevMonthDays = new Date(year, month, 0).getDate();

    const days = [];

    // Prev month padding
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const d = prevMonthDays - i;
      const prevDate = new Date(year, month - 1, d);
      days.push({
        dayNumber: d,
        ymd: formatDateToYMD(prevDate),
        isCurrentMonth: false,
      });
    }

    // Current month days
    for (let d = 1; d <= totalDays; d++) {
      const curDate = new Date(year, month, d);
      days.push({
        dayNumber: d,
        ymd: formatDateToYMD(curDate),
        isCurrentMonth: true,
      });
    }

    // Next month padding to fill standard 35 or 42 grid
    const remaining = (7 - (days.length % 7)) % 7;
    for (let d = 1; d <= remaining; d++) {
      const nextDate = new Date(year, month + 1, d);
      days.push({
        dayNumber: d,
        ymd: formatDateToYMD(nextDate),
        isCurrentMonth: false,
      });
    }

    return {
      year,
      month,
      monthName: MONTH_NAMES[month],
      days,
    };
  };

  const currentMonthData = useMemo(() => {
    return getMonthData(viewDate.getFullYear(), viewDate.getMonth());
  }, [viewDate]);

  const nextMonthDate = useMemo(() => {
    return new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
  }, [viewDate]);

  const nextMonthData = useMemo(() => {
    return getMonthData(nextMonthDate.getFullYear(), nextMonthDate.getMonth());
  }, [nextMonthDate]);

  // Determine active highlight state for a given day
  const getDayState = (ymd) => {
    const isFuture = Boolean(maxDate && ymd > maxDate);
    const isBeforeMin = Boolean(minDate && ymd < minDate);
    const isDisabled = isFuture || isBeforeMin;
    const effectiveEnd = pendingEnd || (pendingStart && hoveredDate && hoveredDate >= pendingStart ? hoveredDate : '');
    const isStart = pendingStart === ymd;
    const isEnd = effectiveEnd === ymd;
    const isInRange = !isDisabled && pendingStart && effectiveEnd && ymd > pendingStart && ymd < effectiveEnd;
    const isToday = ymd === formatDateToYMD(new Date());

    return { isStart, isEnd, isInRange, isToday, isDisabled };
  };

  const displayText = formatDisplayRange(startDate, endDate);
  const hasValue = Boolean(startDate || endDate);

  const renderMonthGrid = (monthData) => (
    <div className="flex-1 min-w-[240px]">
      <div className="text-center font-bold text-sm text-base-content mb-3">
        {monthData.monthName} {monthData.year}
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 text-center mb-1">
        {DAYS_OF_WEEK.map((d) => (
          <span key={d} className="text-[11px] font-black uppercase text-base-content/40 py-1">
            {d}
          </span>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-y-1 gap-x-0.5 text-center">
        {monthData.days.map((item) => {
          const { isStart, isEnd, isInRange, isToday, isDisabled } = getDayState(item.ymd);

          let buttonClasses = 'h-8 w-8 mx-auto text-xs font-bold rounded-lg flex items-center justify-center transition-all ';
          let cellWrapperClasses = 'py-0.5 relative ';

          if (isDisabled) {
            buttonClasses += 'text-base-content/20 opacity-30 cursor-not-allowed select-none pointer-events-none';
          } else if (isStart && isEnd) {
            buttonClasses += 'bg-primary text-primary-content font-black shadow-md scale-105 z-10';
          } else if (isStart) {
            buttonClasses += 'bg-primary text-primary-content font-black shadow-md rounded-r-none z-10';
            cellWrapperClasses += 'bg-primary/10 rounded-l-lg';
          } else if (isEnd) {
            buttonClasses += 'bg-primary text-primary-content font-black shadow-md rounded-l-none z-10';
            cellWrapperClasses += 'bg-primary/10 rounded-r-lg';
          } else if (isInRange) {
            buttonClasses += 'bg-transparent text-primary font-bold';
            cellWrapperClasses += 'bg-primary/15';
          } else if (!item.isCurrentMonth) {
            buttonClasses += 'text-base-content/20 hover:text-base-content/50';
          } else if (isToday) {
            buttonClasses += 'border border-primary/40 text-primary font-black hover:bg-primary/10';
          } else {
            buttonClasses += 'text-base-content/80 hover:bg-base-200 hover:text-base-content';
          }

          return (
            <div key={item.ymd} className={cellWrapperClasses}>
              <button
                type="button"
                disabled={isDisabled}
                onClick={() => !isDisabled && handleDateClick(item.ymd)}
                onMouseEnter={() => {
                  if (!isDisabled && pendingStart && !pendingEnd) {
                    setHoveredDate(item.ymd);
                  }
                }}
                className={buttonClasses}
              >
                {item.dayNumber}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={`relative ${isOpen ? 'z-[60]' : 'z-10'} ${className}`} ref={containerRef}>
      {/* Trigger Button */}
      <div className="relative w-full">
        <CalendarIcon
          size={16}
          className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors ${
            hasValue ? 'text-primary' : 'text-base-content/30'
          }`}
        />

        <input
          type="text"
          readOnly
          value={hasValue ? displayText : ''}
          placeholder={placeholder}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
          className={`w-full pl-9 pr-8 py-2.5 bg-base-100 border rounded-xl text-sm transition-all font-bold cursor-pointer select-none focus:outline-none ${
            isOpen
              ? 'border-primary/50 ring-2 ring-primary/20 bg-base-100'
              : 'border-base-content/10 hover:border-base-content/25'
          } ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
        />

        {hasValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-base-content/30 hover:text-error hover:bg-error/10 transition-all z-10"
            title="Clear date range"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Popover */}
      {isOpen && (
        <div
          ref={popoverRef}
          style={horizontalShift ? { transform: `translateX(${horizontalShift}px)` } : undefined}
          className={`absolute top-full mt-2 z-[200] bg-base-100 border border-base-content/10 rounded-2xl shadow-2xl p-4 overflow-hidden animate-in fade-in zoom-in-95 duration-150 ${
            effectiveAlign === 'right' ? 'right-0' : 'left-0'
          } w-full sm:w-auto max-w-[calc(100vw-2rem)]`}
        >
          {/* Main container with Presets sidebar + Calendars */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Presets Sidebar */}
            <div className="flex md:flex-col gap-1.5 overflow-x-auto pb-2 md:pb-0 md:border-r md:border-base-content/10 md:pr-3 md:min-w-[130px] no-scrollbar">
              <span className="hidden md:block text-[10px] font-black uppercase tracking-widest text-base-content/40 px-2 py-1">
                Presets
              </span>
              {presets.map((preset) => {
                const isActive = startDate === preset.start && endDate === preset.end;
                return (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => handleApplyPreset(preset.start, preset.end)}
                    className={`text-left px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                      isActive
                        ? 'bg-primary text-primary-content shadow-sm'
                        : 'text-base-content/70 hover:bg-base-200 hover:text-base-content'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>

            {/* Calendars area */}
            <div className="flex flex-col gap-3">
              {/* Header Navigation Controls */}
              <div className="flex items-center justify-between px-1">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className="p-1.5 rounded-lg border border-base-content/10 hover:bg-base-200 text-base-content/70 hover:text-base-content transition"
                  title="Previous month"
                >
                  <ChevronLeft size={16} />
                </button>

                <span className="text-[11px] font-black uppercase tracking-widest text-base-content/40">
                  {pendingStart && !pendingEnd ? 'Select end date' : 'Select date range'}
                </span>

                <button
                  type="button"
                  disabled={isNextMonthDisabled}
                  onClick={handleNextMonth}
                  className={`p-1.5 rounded-lg border border-base-content/10 transition ${
                    isNextMonthDisabled
                      ? 'opacity-30 cursor-not-allowed text-base-content/20'
                      : 'hover:bg-base-200 text-base-content/70 hover:text-base-content'
                  }`}
                  title={isNextMonthDisabled ? 'Future dates unavailable' : 'Next month'}
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Month Grids (1 month on mobile, 2 months on sm+ screens) */}
              <div className="flex flex-col sm:flex-row gap-6">
                {renderMonthGrid(currentMonthData)}
                <div className="hidden sm:block">
                  {renderMonthGrid(nextMonthData)}
                </div>
              </div>

              {/* Footer bar */}
              <div className="pt-3 border-t border-base-content/10 flex items-center justify-between gap-2">
                <div className="text-xs font-mono text-base-content/60 truncate max-w-[200px] sm:max-w-none">
                  {pendingStart ? (
                    <span>
                      <strong className="text-base-content">{pendingStart}</strong>
                      {pendingEnd ? ` → ` : ' → ...'}
                      {pendingEnd && <strong className="text-base-content">{pendingEnd}</strong>}
                    </span>
                  ) : (
                    <span className="text-base-content/40">No date range selected</span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-base-content/50 hover:text-base-content hover:bg-base-200 transition"
                  >
                    <RotateCcw size={12} />
                    <span>Reset</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (pendingStart && !pendingEnd) {
                        // If only start date is picked and user clicks Apply, treat as single day range
                        if (onChange) {
                          onChange({ startDate: pendingStart, endDate: pendingStart });
                        }
                      } else if (pendingStart && pendingEnd) {
                        if (onChange) {
                          onChange({ startDate: pendingStart, endDate: pendingEnd });
                        }
                      }
                      setIsOpen(false);
                    }}
                    className="px-4 py-1.5 bg-primary text-primary-content rounded-lg text-xs font-bold hover:bg-primary/90 transition shadow-sm"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DateRangePicker.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'right', 'auto']),
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
};

export default DateRangePicker;
